import { FormEvent, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import DateRangeInput from "components/__reusable/DateRangeInput";
import ClassDropdown from "components/__reusable/ClassDropdown";
import StudentDropdown from "components/__reusable/StudentDropdown";
import HashtagInput from "components/__reusable/HashtagInput";
import IsFavoriteCheckbox from "components/__reusable/IsFavoriteCheckbox";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import SecondaryActionButton from "components/__reusable/SecondaryActionButton";
import useAppDispatch from "hooks/useAppDispatch";
import useAppSelector from "hooks/useAppSelector";
import {
  setDate,
  setClassId,
  setStudentId,
  setHashtag,
  setIsFavorite,
  reset,
} from "features/pictureSearch/pictureSearchSlice";
import { PictureStudentOption } from "config/constants";
import { PICTURES_RESULTS_PATH } from "router/pathName";
import { searchHashTag } from "services/pictureServices";
import SearchIcon from "assets/icons/SearchIcon";
import { IoIosShareAlt } from "react-icons/io";

const TODAY = new Date();
TODAY.setHours(23, 59, 59, 999);

const normalizeHashtagValue = (value: string) =>
  value.replace(/^#/, "").trim().normalize("NFKC");

const FilterForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { startDate, endDate, classId, studentId, hashtagId, isFavorite } =
    useAppSelector((state) => state.pictureSearch);
  const [hashTagInputValue, setHashTagInputValue] = useState("");
  const dispatch = useAppDispatch();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const startDateParam = params.get("startDate");
    const endDateParam = params.get("endDate");
    const classIdParam = params.get("classId");
    const studentIdParam = params.get("studentId");
    const hashtagIdParam = params.get("hashtagId");
    const favoriteParam = params.get("isFavorite");

    if (startDateParam && endDateParam) {
      dispatch(setDate({ startDate: startDateParam, endDate: endDateParam }));
    }

    if (classIdParam) {
      dispatch(setClassId({ classId: Number(classIdParam) }));
    }

    if (studentIdParam) {
      const num = Number(studentIdParam);
      dispatch(
        setStudentId({ studentId: isNaN(num) ? (studentIdParam as any) : num }),
      );
    }

    if (hashtagIdParam) {
      dispatch(setHashtag({ hashtagId: Number(hashtagIdParam) }));
    }

    if (favoriteParam) {
      dispatch(setIsFavorite({ isFavorite: favoriteParam === "true" }));
    }
  }, [location.search, dispatch]);

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();

    let resolvedHashtagId = hashtagId;
    if (hashTagInputValue.length > 0 && !hashtagId) {
      const hashtags = await searchHashTag(hashTagInputValue);
      const normalizedInput = normalizeHashtagValue(hashTagInputValue);
      const id =
        hashtags.find(
          (tag) => normalizeHashtagValue(tag.name) === normalizedInput,
        )?.id || null;

      resolvedHashtagId = id;
      dispatch(
        setHashtag({
          hashtagId: id,
        }),
      );
    }

    const params = new URLSearchParams();

    // Format dates to YYYY-MM-DD avoiding timezone issues
    const formatToDateString = (isoString: string) => {
      const date = new Date(isoString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    if (startDate) params.set("startDate", formatToDateString(startDate));
    if (endDate) params.set("endDate", formatToDateString(endDate));
    if (classId) params.set("classId", classId.toString());
    if (studentId) params.set("studentId", studentId.toString());
    if (resolvedHashtagId)
      params.set("hashtagId", resolvedHashtagId.toString());
    if (isFavorite !== null) params.set("isFavorite", isFavorite.toString());
    navigate(`${PICTURES_RESULTS_PATH}?${params.toString()}`);
  };

  return (
    <form className="space-y-3 lg:space-y-5" onSubmit={submitForm}>
      <h2 className="text-xl font-semibold">
        {t("_published_picture._search_pictures")}
      </h2>
      <div className="w-full">
        <DateRangeInput
          name={""}
          currentStartDate={new Date(startDate)}
          currentEndDate={new Date(endDate)}
          maxDate={TODAY}
          onChange={(dates: Date[]) => {
            const start = dates[0].toISOString();
            const end = dates[1].toISOString();
            dispatch(
              setDate({
                startDate: start,
                endDate: end,
              }),
            );
          }}
        />
      </div>
      <div className="w-full">
        <ClassDropdown
          currentClassId={classId?.toString() || ""}
          onChange={(id) => {
            dispatch(setClassId({ classId: id }));
            dispatch(setStudentId({ studentId: PictureStudentOption.all }));
          }}
        />
      </div>
      {classId && (
        <div className="w-full">
          <StudentDropdown
            currentClassId={classId || 0}
            currentStudentId={studentId?.toString() || ""}
            onChange={(id) => {
              dispatch(setStudentId({ studentId: id }));
            }}
          />
        </div>
      )}
      <div className="w-full">
        <HashtagInput
          inputValue={hashTagInputValue}
          onInputChange={(value) => setHashTagInputValue(value)}
          value={hashtagId}
          onChange={(value) => {
            dispatch(setHashtag({ hashtagId: value }));
          }}
        />
      </div>
      <div className="w-full">
        <IsFavoriteCheckbox
          isFavorite={isFavorite || false}
          onChange={(status) => {
            dispatch(setIsFavorite({ isFavorite: status }));
          }}
        />
      </div>
      <div className="w-full md:flex md:space-x-3 md:items-center md:justify-end">
        <div className="flex justify-end mb-2 md:mb-0">
          <div className="w-[10rem]">
            <SecondaryActionButton
              label={t("_published_picture._clear")}
              icon={
                <div style={{ transform: "scaleX(-1)" }}>
                  <IoIosShareAlt size={18} />
                </div>
              }
              onClick={() => {
                dispatch(reset());
                setHashTagInputValue("");
              }}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <div className="min-w-[180px] md:w-[180px]">
            <PrimaryActionButton
              isButton={false}
              label={t("_published_picture._search")}
              disabled={!classId || !studentId}
              leftFloatingIcon={
                <SearchIcon width={24} height={24} color={"white"} />
              }
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default FilterForm;
